import React, { useState, useMemo, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { useCollection } from '../../../hooks/useCollection';
import { sendTelegramNotification, formatDateFR } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import { Trash2 } from 'lucide-react';

export default function ReservationsPage({ settings }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const { agencyId: authAgencyId } = useAuth();
  const agencyId = authAgencyId || (typeof window !== 'undefined' ? window.__AGENCY_ID__ : null);

  const { data: reservations, loading, remove, update } = useCollection('reservations');
  const { add: addRental } = useCollection('rentals');
  const { update: updateCar } = useCollection('cars');

  const [filterStatus, setFilterStatus] = useState('pending');
  const [showQR, setShowQR] = useState(false);
  const canvasRef = useRef(null);

  const reservationUrl = `${window.location.origin}${window.location.pathname}?reserve&agency=${agencyId || ''}`;

  useEffect(() => {
    if (showQR && canvasRef.current && reservationUrl) {
      QRCode.toCanvas(
        canvasRef.current,
        reservationUrl,
        { width: 220, margin: 2, color: { dark: '#000000', light: '#ffffff' } },
        (err) => {
          if (err) console.error('[QR] Canvas render failed:', err);
        }
      );
    }
  }, [showQR, reservationUrl]);

  const downloadQR = () => {
    if (!canvasRef.current) return;
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = 'qr-reservation.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('[QR] Download failed:', err);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await update(id, { 
        status, 
        [`${status}At`]: new Date().toISOString() 
      });
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const confirmAndBook = async (res) => {
    try {
      // 1. Mark reservation confirmed
      await update(res.id, { 
        status: 'confirmed', 
        confirmedAt: new Date().toISOString() 
      });

      // 2. Create rental entry so car blocks in calendar
      await addRental({
        clientId: '',
        clientName: res.clientName,
        clientPhone: res.clientPhone || '',
        clientCin: res.clientCin || '',
        carId: res.carId,
        carName: res.carName,
        carPlate: res.carPlate || '',
        startDate: res.startDate,
        endDate: res.endDate,
        totalAmount: res.totalPrice || 0,
        dailyRate: res.carDailyPrice || res.dailyRate || 0,
        totalDays: res.totalDays || 0,
        status: 'active',
        source: 'reservation',
        reservationId: res.id,
        createdAt: new Date().toISOString()
      });

      // 3. Update car status
      if (res.carId) {
        await updateCar(res.carId, { status: 'rented' });
      }

      // 4. Notify via Telegram
      await sendTelegramNotification(
        buildTelegramMessage('new_reservation', {
          clientName: res.clientName, carName: res.carName,
          startDate: res.startDate, endDate: res.endDate
        }),
        'new_reservation'
      );

      alert(isAr ? '✅ تم التأكيد وإضافة العقد في التقويم!' : '✅ Confirmé et ajouté au calendrier!');
    } catch (err) { 
      alert('❌ ' + err.message); 
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(isAr ? 'حفظ الحجز؟' : 'Supprimer cette réservation?')) return; // keeping matching alerts
    try {
      await remove(id);
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(reservationUrl);
    alert(isAr ? '✅ تم نسخ الرابط' : '✅ Lien copié');
  };

  const sendWhatsApp = (res) => {
    const msg = isAr
      ? `السلام عليكم ${res.clientName}\n\nتم تأكيد حجزكم للسيارة ${res.carName} من ${formatDateFR(res.startDate)} إلى ${formatDateFR(res.endDate)}.\n\nالمجموع: ${res.totalPrice} درهم\n\nشكراً لاختياركم ${settings.companyName || ''}`
      : `Bonjour ${res.clientName},\n\nVotre réservation pour ${res.carName} du ${formatDateFR(res.startDate)} au ${formatDateFR(res.endDate)} est confirmée.\n\nTotal: ${res.totalPrice} ${settings.currency || 'MAD'}\n\nMerci de votre confiance.\n${settings.companyName || ''}`;
    const phone = (res.clientPhone || '').replace(/[\s+]/g, '');
    const waUrl = `https://wa.me/${phone.startsWith('212') ? phone : '212' + phone.replace(/^0/, '')}?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank');
  };

  const stats = useMemo(() => ({
    pending: reservations.filter(r => r.status === 'pending').length,
    confirmed: reservations.filter(r => r.status === 'confirmed').length,
    refused: reservations.filter(r => r.status === 'refused').length,
    converted: reservations.filter(r => r.status === 'converted').length
  }), [reservations]);

  const filtered = useMemo(() => {
    const result = filterStatus === 'all' ? reservations : reservations.filter(r => r.status === filterStatus);
    return [...result].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [reservations, filterStatus]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          🎫 {isAr ? `الحجوزات (${reservations.length})` : `Réservations (${reservations.length})`}
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowQR(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-bold text-sm transition"
          >
            📱 {isAr ? 'رمز QR' : 'QR Code'}
          </button>
          <button
            onClick={copyLink}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-sm transition"
          >
            🔗 {isAr ? 'نسخ الرابط' : 'Copier lien'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="text-[10px] font-bold text-amber-700 uppercase">⏳ {isAr ? 'في الانتظار' : 'En attente'}</div>
          <div className="text-2xl font-black text-amber-900">{stats.pending}</div>
        </div>
        <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
          <div className="text-[10px] font-bold text-green-700 uppercase">✅ {isAr ? 'مؤكدة' : 'Confirmées'}</div>
          <div className="text-2xl font-black text-green-900">{stats.confirmed}</div>
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="text-[10px] font-bold text-blue-700 uppercase">🔄 {isAr ? 'محولة' : 'Converties'}</div>
          <div className="text-2xl font-black text-blue-900">{stats.converted}</div>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
          <div className="text-[10px] font-bold text-red-700 uppercase">❌ {isAr ? 'مرفوضة' : 'Refusées'}</div>
          <div className="text-2xl font-black text-red-900">{stats.refused}</div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="mb-3 flex flex-wrap gap-2">
        {['pending', 'confirmed', 'converted', 'refused', 'all'].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${
              filterStatus === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s === 'all' 
              ? (isAr ? 'الكل' : 'Toutes') 
              : s === 'pending' 
              ? (isAr ? 'انتظار' : 'En attente') 
              : s === 'confirmed' 
              ? (isAr ? 'مؤكدة' : 'Confirmées') 
              : s === 'converted' 
              ? (isAr ? 'محولة' : 'Converties') 
              : (isAr ? 'مرفوضة' : 'Refusées')}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-5xl mb-3">🎫</div>
          <p className="text-gray-500 font-medium">
            {isAr ? 'لا توجد حجوزات' : 'Aucune réservation'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(r => {
            const statusBadge = {
              pending: { color: 'bg-amber-500', text: isAr ? '⏳ في الانتظار' : '⏳ En attente' },
              confirmed: { color: 'bg-green-500', text: isAr ? '✅ مؤكدة' : '✅ Confirmée' },
              converted: { color: 'bg-blue-500', text: isAr ? '🔄 محولة' : '🔄 Convertie' },
              refused: { color: 'bg-red-500', text: isAr ? '❌ مرفوضة' : '❌ Refusée' }
            }[r.status] || { color: 'bg-gray-500', text: r.status };

            return (
              <div
                key={r.id}
                className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xl">🎫</span>
                      <span className="font-bold text-gray-900">{r.clientName}</span>
                      <span className={`px-2 py-0.5 ${statusBadge.color} text-white text-[10px] font-bold rounded-full`}>
                        {statusBadge.text}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div className="font-mono">🚗 {r.carName} ({r.carPlate})</div>
                      <div>📞 {r.clientPhone} • 🆔 {r.clientCin}</div>
                      {r.clientEmail && <div>✉️ {r.clientEmail}</div>}
                      <div className="font-mono">
                        📅 {formatDateFR(r.startDate)} → {formatDateFR(r.endDate)}
                        {' • ⏱️ '}{r.totalDays}{isAr ? ' يوم' : ' j'}
                      </div>
                      <div className="font-bold text-green-700">💰 {r.totalPrice} {settings.currency || 'MAD'}</div>
                      {r.notes && <div className="italic text-gray-500">📝 {r.notes}</div>}
                      <div className="text-[10px] text-gray-400">
                        🕐 {r.createdAt ? new Date(r.createdAt).toLocaleString('fr-FR') : '-'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    {r.status === 'pending' && (
                      <button
                        onClick={() => confirmAndBook(r)}
                        className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded transition"
                      >
                        ✅ {isAr ? 'تأكيد' : 'Confirmer'}
                      </button>
                    )}
                    {r.status === 'pending' && (
                      <button
                        onClick={() => updateStatus(r.id, 'refused')}
                        className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold rounded transition"
                      >
                        ❌ {isAr ? 'رفض' : 'Refuser'}
                      </button>
                    )}
                    {r.status === 'confirmed' && (
                      <button
                        onClick={() => sendWhatsApp(r)}
                        className="px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 text-xs font-bold rounded transition"
                      >
                        💬 WhatsApp
                      </button>
                    )}
                    {r.status === 'confirmed' && (
                      <button
                        onClick={() => updateStatus(r.id, 'converted')}
                        className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-bold rounded transition"
                      >
                        🔄 {isAr ? 'محول' : 'Convertir'}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(r.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition self-end"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* QR Modal */}
      {showQR && (
        <div
          onClick={() => setShowQR(false)}
          className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl"
          >
            <h2 className="text-xl font-bold mb-3">📱 {isAr ? 'رمز QR للحجز' : 'QR Code Réservation'}</h2>
            <p className="text-sm text-gray-500 mb-4">
              {isAr ? 'العملاء يمكنهم مسح هذا الرمز للحجز' : 'Les clients scannent ce code pour réserver'}
            </p>

            <div className="flex items-center justify-center mb-4">
              <canvas
                ref={canvasRef}
                className="border-4 border-gray-100 rounded-xl bg-white shadow-sm max-w-[220px] h-auto"
              />
            </div>

            <div className="mb-4 p-2 bg-gray-50 rounded-lg font-mono text-xs text-gray-600 break-all select-all border border-gray-100">
              {reservationUrl}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={downloadQR}
                className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg transition text-center cursor-pointer"
              >
                ⬇️ {isAr ? 'تحميل QR' : 'Télécharger'}
              </button>
              <button
                type="button"
                onClick={copyLink}
                className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition cursor-pointer"
              >
                🔗 {isAr ? 'نسخ الرابط' : 'Copier'}
              </button>
              <button
                type="button"
                onClick={() => setShowQR(false)}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition cursor-pointer"
              >
                {isAr ? 'إغلاق' : 'Fermer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
